import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchWithAuth, getCurrentOrganization } from './lib/api';

// Δημιουργία context
const PositionsContext = createContext();

export const usePositions = () => useContext(PositionsContext);

export const PositionsProvider = ({ children }) => {
  const [positions, setPositions] = useState([]);
  const [shifts, setShifts] = useState([]); 
  const [loading, setLoading] = useState(true);

  // Φόρτωση δεδομένων από backend
  const fetchPositions = useCallback(async () => {
    try {
      // Wait for organization to be set
      const orgId = getCurrentOrganization();
      if (!orgId) {
        console.log('PositionsContext: Waiting for organization...');
        return;
      }
      
      const res = await fetchWithAuth('/api/positions');
      if (res.ok) {
        const data = await res.json();
        console.log('Loaded positions:', data);
        setPositions(data.map(p => p.name));
      } else {
        console.error('Failed to load positions:', res.status);
      }
    } catch (error) {
      console.error('Error loading positions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and refetch when organization changes
  useEffect(() => {
    // Φόρτωση shift preferences από localStorage ή χρήση default
    const savedShifts = localStorage.getItem('shiftPreferences');
    if (savedShifts) {
      setShifts(JSON.parse(savedShifts));
    } else {
      const defaultShifts = ['Πρωινη', 'Βραδινη'];
      setShifts(defaultShifts);
      localStorage.setItem('shiftPreferences', JSON.stringify(defaultShifts));
    }

    // Initial fetch - retry if organization not ready
    const tryFetchPositions = () => {
      const orgId = getCurrentOrganization();
      if (orgId) {
        fetchPositions();
      } else {
        // Retry after a short delay if organization not set yet
        setTimeout(tryFetchPositions, 500);
      }
    };
    tryFetchPositions();
    
    // Listen for organization changes
    const handleOrgChange = () => {
      setLoading(true);
      fetchPositions();
    };
    
    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, [fetchPositions]);

  // Προσθήκη νέου πόστου
  const addPosition = async (newPosition) => {
    if (!newPosition || positions.includes(newPosition)) return false;
    const res = await fetchWithAuth('/api/positions', {
      method: 'POST',
      body: JSON.stringify({ name: newPosition })
    });
    if (res.ok) {
      setPositions([...positions, newPosition]);
      return true;
    }
    return false;
  };

  // Διαγραφή πόστου
  const removePosition = async (positionToRemove) => {
    // Βρες το id από το backend
    const res = await fetchWithAuth('/api/positions');
    if (res.ok) {
      const data = await res.json();
      const pos = data.find(p => p.name === positionToRemove);
      if (pos) {
        await fetchWithAuth(`/api/positions/${pos.id}`, { method: 'DELETE' });
        setPositions(positions.filter(pos => pos !== positionToRemove));
      }
    }
  };

  // Προσθήκη νέας βάρδιας
  const addShift = async (newShift) => {
    if (!newShift || shifts.includes(newShift)) return false;
    const updatedShifts = [...shifts, newShift];
    setShifts(updatedShifts);
    localStorage.setItem('shiftPreferences', JSON.stringify(updatedShifts));
    return true;
  };

  // Διαγραφή βάρδιας
  const removeShift = async (shiftToRemove) => {
    if (shifts.length <= 1) return false; // Κράτα τουλάχιστον μία βάρδια
    const updatedShifts = shifts.filter(shift => shift !== shiftToRemove);
    setShifts(updatedShifts);
    localStorage.setItem('shiftPreferences', JSON.stringify(updatedShifts));
    return true;
  };

  // Refresh positions (can be called externally)
  const refreshPositions = useCallback(() => {
    setLoading(true);
    fetchPositions();
  }, [fetchPositions]);

  return (
    <PositionsContext.Provider value={{
      positions,
      shifts,
      addPosition,
      removePosition,
      addShift,
      removeShift,
      loading,
      refreshPositions
    }}>
      {children}
    </PositionsContext.Provider>
  );
};
